import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Entity } from 'ag-common-lib/public-api';
import { hasPermissionGuard } from 'ag-common-svc/shared/utils/has-permission-guard';
import { ARMAuthService } from 'src/app/shared/services/util/arm-auth.service';
import { AgentResearchApiService } from './agent-research-api.service';
import {
  AGENT_RESEARCH_FIELD_OPTIONS,
  AGENT_RESEARCH_SOURCE_OPTIONS,
  AgentResearchField,
  AgentResearchFact,
  AgentResearchMatch,
  AgentResearchSearchResponse,
  AgentResearchSource,
} from './agent-research.models';

export interface ResultCard {
  key: string;
  scorePercent: number;
  band: 'high' | 'mid' | 'low';
  name: string;
  isOrg: boolean;
  sourceLabel: string;
  sourceKind: 'registry' | 'downline';
  matchedKind: string;
  agentId: number | null;
  factItems: { label: string; value: string; muted: boolean }[];
  facts: AgentResearchFact[];
  expanded: boolean;
  /**
   * Whether this person is in the carrier's CURRENT downline file. 'unknown' when
   * there is nothing current to compare against (no active file, or a registry
   * record) — which must not read as "dropped".
   */
  production: 'current' | 'dropped' | 'unknown';
  /** Explains the badge on hover: which file, and when they were last in one. */
  productionHint: string | null;
}

// Source names must be unambiguous: 'registry' is platform.agent_registry (the
// p_agent_id authority behind the Agent IDs screen), NOT the platform.agents
// records table — "known agent" wrongly implied the latter.
const SOURCE_LABELS: Record<string, string> = {
  registry: 'agent registry',
  downline_nlg: 'carrier downline',
  downline_anico: 'carrier downline',
};

// Search-as-you-type: fire one request after the user pauses this long, and only
// for terms of at least this length (a single character matches far too much).
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SEARCH_LIMIT = 25;
const HIGH_BAND_MIN = 85;
const MID_BAND_MIN = 60;

/**
 * State and behaviour for the agent-research launcher. The component is a thin
 * view over this — it only owns DOM concerns (focus, keyboard). Provided at the
 * component level so its state and debounce timer share the component lifecycle.
 */
@Injectable()
export class AgentResearchLauncherService implements OnDestroy {
  private readonly authService = inject(ARMAuthService);
  private readonly api = inject(AgentResearchApiService);

  readonly fieldOptions = AGENT_RESEARCH_FIELD_OPTIONS;
  readonly sourceOptions = AGENT_RESEARCH_SOURCE_OPTIONS;

  readonly panelVisible = signal(false);
  readonly filtersOpen = signal(false);
  readonly term = signal('');
  readonly overrideFields = signal<AgentResearchField[] | null>(null); // null = auto-detect
  readonly threshold = signal(0.4);
  readonly sources = signal<AgentResearchSource[]>(['registry', 'downline']);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly response = signal<AgentResearchSearchResponse | null>(null);
  readonly expanded = signal<ReadonlySet<string>>(new Set());

  readonly isAuto = computed(() => this.overrideFields() === null);

  // Read permission on the entity offers the tool (same gate as the header menus).
  private readonly canUse$ = this.authService.armPermissions$.pipe(
    map(permissions => hasPermissionGuard([Entity.agentResearchTool], permissions)),
  );
  readonly canUse = toSignal(this.canUse$, { initialValue: false });

  readonly cards = computed<ResultCard[]>(() => {
    const current = this.response();
    if (current === null) {
      return [];
    }
    const expanded = this.expanded();
    return current.matches.map(match => this.toCard(match, expanded));
  });

  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchSeq = 0;

  openPanel(): void {
    this.panelVisible.set(true);
  }

  closePanel(): void {
    this.panelVisible.set(false);
  }

  toggleFilters(): void {
    this.filtersOpen.set(!this.filtersOpen());
  }

  onTermInput(value: string): void {
    this.term.set(value);
    this.scheduleDebouncedSearch();
  }

  setAuto(): void {
    this.overrideFields.set(null);
    this.rerunIfActive();
  }

  isFieldSelected(field: AgentResearchField): boolean {
    const fields = this.overrideFields();
    if (fields === null) {
      return false;
    }
    return fields.includes(field);
  }

  toggleField(field: AgentResearchField): void {
    const current = this.currentOverrideFields();
    const index = current.indexOf(field);
    if (index >= 0) {
      current.splice(index, 1);
    }
    if (index < 0) {
      current.push(field);
    }
    this.commitOverrideFields(current);
    this.rerunIfActive();
  }

  isSourceSelected(source: AgentResearchSource): boolean {
    return this.sources().includes(source);
  }

  toggleSource(source: AgentResearchSource): void {
    const current = [...this.sources()];
    const index = current.indexOf(source);
    if (index >= 0) {
      current.splice(index, 1);
    }
    if (index < 0) {
      current.push(source);
    }
    this.sources.set(current);
    this.rerunIfActive();
  }

  onThresholdChanged(value: number | string): void {
    let parsed = Number(value);
    if (Number.isNaN(parsed)) {
      parsed = 0;
    }
    const clamped = Math.max(0, Math.min(1, parsed));
    this.threshold.set(clamped);
  }

  // Fired on the slider's `change` (release), not every `input` tick, so we
  // re-search once the user settles on an accuracy rather than on every pixel.
  onThresholdCommit(): void {
    this.rerunIfActive();
  }

  toggleExpanded(key: string): void {
    const next = new Set(this.expanded());
    if (next.has(key)) {
      next.delete(key);
      this.expanded.set(next);
      return;
    }
    next.add(key);
    this.expanded.set(next);
  }

  trackByKey(_index: number, card: ResultCard): string {
    return card.key;
  }

  async search(): Promise<void> {
    // Cancel any pending debounced fire — this call supersedes it.
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    const term = this.term().trim();
    if (term.length === 0) {
      this.error.set('Enter a name, email, phone, or id.');
      return;
    }
    if (this.sources().length === 0) {
      this.error.set('Select at least one source.');
      return;
    }
    // Tag this request; a later search bumps the sequence so this one's response
    // is discarded if it arrives out of order (keeps the newest query's results).
    const seq = ++this.searchSeq;
    this.loading.set(true);
    this.error.set(null);
    try {
      const response = await this.api.search({
        term,
        fields: this.requestFields(),
        threshold: this.threshold(),
        sources: this.sources(),
        limit: SEARCH_LIMIT,
      });
      if (seq !== this.searchSeq) {
        return;
      }
      this.response.set(response);
      this.expanded.set(new Set());
    } catch (error) {
      if (seq !== this.searchSeq) {
        return;
      }
      this.response.set(null);
      this.error.set(this.toMessage(error));
    } finally {
      if (seq === this.searchSeq) {
        this.loading.set(false);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
    }
  }

  /**
   * Debounce keystrokes into a single search once the user pauses. An empty box
   * clears results immediately; a 1-char term is ignored (too broad). search()
   * shares the same timer-cancel + sequence guard so an explicit Enter/click and
   * the debounced fire can never race or apply a stale response.
   */
  private scheduleDebouncedSearch(): void {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    const term = this.term().trim();
    if (term.length === 0) {
      // Discard any in-flight response so it can't repopulate the cleared box.
      this.searchSeq++;
      this.clearResults();
      return;
    }
    if (term.length < MIN_QUERY_LENGTH) {
      return;
    }
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      void this.search();
    }, SEARCH_DEBOUNCE_MS);
  }

  private clearResults(): void {
    if (this.response() === null && this.error() === null) {
      return;
    }
    this.response.set(null);
    this.error.set(null);
    this.expanded.set(new Set());
  }

  private rerunIfActive(): void {
    // A deliberate filter change re-applies immediately when there is an active
    // query with results; otherwise there is nothing to refresh yet.
    if (this.term().trim().length === 0) {
      return;
    }
    if (this.response() === null) {
      return;
    }
    void this.search();
  }

  private currentOverrideFields(): AgentResearchField[] {
    const fields = this.overrideFields();
    if (fields === null) {
      return [];
    }
    return [...fields];
  }

  private commitOverrideFields(fields: AgentResearchField[]): void {
    if (fields.length === 0) {
      this.overrideFields.set(null);
      return;
    }
    this.overrideFields.set(fields);
  }

  private requestFields(): AgentResearchField[] | undefined {
    const override = this.overrideFields();
    if (override === null) {
      return undefined;
    }
    return override;
  }

  private toCard(match: AgentResearchMatch, expanded: ReadonlySet<string>): ResultCard {
    const key = `${match.recordType}:${match.recordId}`;
    const facts = this.factsOf(match);
    return {
      key,
      scorePercent: Math.round(match.scorePercent),
      band: this.scoreBand(match.scorePercent),
      name: this.matchName(match),
      isOrg: match.details?.entityKind === 'org',
      sourceLabel: this.sourceLabel(match.source),
      sourceKind: match.recordType === 'downline' ? 'downline' : 'registry',
      matchedKind: match.matchedKind,
      agentId: match.agentId,
      factItems: this.buildFactItems(match, facts),
      facts,
      expanded: expanded.has(key),
      production: this.productionState(match),
      productionHint: this.productionHint(match),
    };
  }

  /**
   * Is this person in the carrier's downline right now, or only in older files?
   * The server answers null when it cannot say (no active file, or a registry
   * record), and 'unknown' keeps that distinct from a confirmed drop-out.
   */
  private productionState(match: AgentResearchMatch): 'current' | 'dropped' | 'unknown' {
    const inActiveFile = match.details?.inActiveFile;
    if (inActiveFile === true) {
      return 'current';
    }
    if (inActiveFile === false) {
      return 'dropped';
    }
    return 'unknown';
  }

  private productionHint(match: AgentResearchMatch): string | null {
    const details = match.details;
    if (!details || details.inActiveFile === null || details.inActiveFile === undefined) {
      return null;
    }

    if (details.inActiveFile) {
      return details.activeReportDate
        ? `In the current downline file (${details.activeReportDate})`
        : 'In the current downline file';
    }

    return details.lastSeen
      ? `Not in the current downline file${details.activeReportDate ? ` (${details.activeReportDate})` : ''} — last seen ${details.lastSeen}`
      : 'Not in the current downline file';
  }

  private factsOf(match: AgentResearchMatch): AgentResearchFact[] {
    if (!match.details) {
      return [];
    }
    return match.details.facts;
  }

  /** Collapsed-card facts as label/value pairs; agency leads because it is the
   *  field operators match on. Labels render bold, items split by a divider. */
  private buildFactItems(
    match: AgentResearchMatch,
    facts: AgentResearchFact[],
  ): { label: string; value: string; muted: boolean }[] {
    const items: { label: string; value: string; muted: boolean }[] = [];
    const push = (label: string, value: string | null, muted = false) => {
      if (value !== null && value.length > 0) {
        items.push({ label, value, muted });
      }
    };
    push('agency', this.firstFactValue(facts, 'agency'));
    push('email', this.firstFactValue(facts, 'email'));
    push('phone', this.formatPhone(this.firstFactValue(facts, 'phone')));
    push('address', this.firstFactValue(facts, 'address'), true);
    push('NPN', match.details?.npn ?? null, true);
    if (match.details?.seenInFiles != null) {
      push('seen', `${match.details.seenInFiles} file(s)`, true);
    }
    return items;
  }

  private scoreBand(scorePercent: number): 'high' | 'mid' | 'low' {
    if (scorePercent >= HIGH_BAND_MIN) {
      return 'high';
    }
    if (scorePercent >= MID_BAND_MIN) {
      return 'mid';
    }
    return 'low';
  }

  private matchName(match: AgentResearchMatch): string {
    if (match.displayName === null) {
      return '(unnamed)';
    }
    return match.displayName;
  }

  private sourceLabel(source: string): string {
    const label = SOURCE_LABELS[source];
    if (label === undefined) {
      return source;
    }
    return label;
  }

  private firstFactValue(facts: AgentResearchFact[], kind: string): string | null {
    const fact = facts.find(entry => entry.kind === kind);
    if (fact === undefined) {
      return null;
    }
    const value = fact.values[0];
    if (value === undefined) {
      return null;
    }
    return value;
  }

  private formatPhone(value: string | null): string | null {
    if (value === null) {
      return null;
    }
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 10) {
      return value;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private toMessage(error: unknown): string {
    if (error !== null && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return 'Search failed. Please try again.';
  }
}
